import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { 
  Target, Plus, Lightbulb, Building2, Check, X, ChevronDown, ChevronUp, 
  MessageSquare, Send, Trophy, Star, AlertCircle 
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const DEPARTMENTS = {
  company: { label: "Company-Wide", color: "bg-slate-600", textColor: "text-slate-300" },
  sales: { label: "Sales", color: "bg-indigo-600", textColor: "text-indigo-400" },
  csm: { label: "Customer Success", color: "bg-emerald-600", textColor: "text-emerald-400" },
  dev: { label: "Development", color: "bg-purple-600", textColor: "text-purple-400" },
  support: { label: "Support", color: "bg-amber-600", textColor: "text-amber-400" },
  marketing: { label: "Marketing", color: "bg-pink-600", textColor: "text-pink-400" },
};

const CompanyRocks = () => {
  const { token, user, isAdmin } = useAuth();
  const [rocks, setRocks] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [myIdeas, setMyIdeas] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [showIdeaDialog, setShowIdeaDialog] = useState(false);
  const [showRockDialog, setShowRockDialog] = useState(false);
  const [showIdeasPanel, setShowIdeasPanel] = useState(false);
  const [newIdea, setNewIdea] = useState("");
  const [newRock, setNewRock] = useState({ title: "", description: "", department: "company", priority: 1 });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const headers = { Authorization: `Bearer ${token}` };
  const userDepartment = user?.department || "sales";

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rocksRes, ideasRes] = await Promise.all([
        axios.get(`${API}/company-rocks?month=${selectedMonth}&year=${selectedYear}`, { headers }),
        axios.get(`${API}/rock-ideas?month=${selectedMonth}&year=${selectedYear}`, { headers }),
      ]);
      setRocks(rocksRes.data);
      
      if (isAdmin) {
        setIdeas(ideasRes.data);
      } else {
        setMyIdeas(ideasRes.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitIdea = async () => {
    if (!newIdea.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/rock-ideas`, {
        idea: newIdea,
        month: selectedMonth,
        year: selectedYear
      }, { headers });
      setMessage({ type: "success", text: "Your idea has been submitted!" });
      setNewIdea("");
      setShowIdeaDialog(false);
      fetchData();
    } catch (error) {
      setMessage({ type: "error", text: "Failed to submit idea" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateRock = async () => {
    if (!newRock.title.trim() || !newRock.description.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/company-rocks`, {
        ...newRock,
        month: selectedMonth,
        year: selectedYear
      }, { headers });
      setMessage({ type: "success", text: "Rock created successfully!" });
      setNewRock({ title: "", description: "", department: "company", priority: 1 });
      setShowRockDialog(false);
      fetchData();
    } catch (error) {
      setMessage({ type: "error", text: "Failed to create rock" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateIdeaStatus = async (ideaId, status) => {
    try {
      await axios.put(`${API}/rock-ideas/${ideaId}/status?status=${status}`, {}, { headers });
      fetchData();
    } catch (error) {
      console.error("Error updating idea status:", error);
    }
  };

  const handleDeleteRock = async (rockId) => {
    if (!window.confirm("Delete this rock?")) return;
    try {
      await axios.delete(`${API}/company-rocks/${rockId}`, { headers });
      fetchData();
    } catch (error) {
      console.error("Error deleting rock:", error);
    }
  };

  // Group rocks by department
  const companyRocks = rocks.filter(r => r.department === "company");
  const deptRocks = rocks.filter(r => r.department !== "company");
  const myDeptRocks = rocks.filter(r => r.department === userDepartment || r.department === "company");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="company-rocks-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-lg font-bold text-slate-100 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Target className="w-5 h-5 text-indigo-400" />
            Rocks
          </h1>
          <p className="text-xs text-slate-400">{MONTHS[selectedMonth - 1]} {selectedYear}</p>
        </div>

        <div className="flex gap-2">
          {/* Month/Year Selectors */}
          <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(Number(v))}>
            <SelectTrigger className="w-[100px] h-8 text-xs bg-slate-800 border-slate-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {MONTHS.map((month, idx) => (
                <SelectItem key={month} value={(idx + 1).toString()} className="text-slate-200 text-xs">
                  {month}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="h-8 px-2 text-xs bg-slate-800 border border-slate-700 rounded-md text-white"
          >
            {[2024, 2025, 2026, 2027].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          {/* Submit Idea Button */}
          <Button
            size="sm"
            onClick={() => setShowIdeaDialog(true)}
            className="bg-amber-600 hover:bg-amber-700 h-8"
          >
            <Lightbulb size={14} className="mr-1" />
            Share Idea
          </Button>

          {/* Admin: Create Rock */}
          {isAdmin && (
            <Button
              size="sm"
              onClick={() => setShowRockDialog(true)}
              className="bg-indigo-600 hover:bg-indigo-700 h-8"
            >
              <Plus size={14} className="mr-1" />
              Add Rock
            </Button>
          )}
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`mb-4 p-2 rounded-lg text-sm flex items-center gap-2 ${
          message.type === "success" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"
        }`}>
          {message.type === "success" ? <Check size={14} /> : <AlertCircle size={14} />}
          {message.text}
        </div>
      )}

      {/* Company-Wide Rocks */}
      {companyRocks.length > 0 && (
        <div className="mb-4">
          <h2 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-2">
            <Trophy size={14} className="text-amber-400" />
            Company-Wide Rocks
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {companyRocks.map(rock => (
              <div key={rock.id} className="p-3 bg-slate-800/50 border border-slate-600 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 mb-1">
                    <Star size={14} className={rock.priority === 1 ? "text-amber-400" : rock.priority === 2 ? "text-slate-400" : "text-slate-600"} />
                    <h3 className="font-semibold text-slate-100 text-sm">{rock.title}</h3>
                  </div>
                  {isAdmin && (
                    <button onClick={() => handleDeleteRock(rock.id)} className="text-slate-500 hover:text-red-400">
                      <X size={14} />
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-400">{rock.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Department Rocks */}
      {isAdmin ? (
        // Admin sees all departments
        <div className="space-y-4">
          {Object.entries(DEPARTMENTS).filter(([key]) => key !== "company").map(([dept, config]) => {
            const dRocks = rocks.filter(r => r.department === dept);
            if (dRocks.length === 0) return null;
            return (
              <div key={dept}>
                <h2 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${config.color}`}></div>
                  {config.label} Rocks
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {dRocks.map(rock => (
                    <div key={rock.id} className={`p-3 border rounded-lg bg-slate-800/30`} style={{ borderColor: `${config.color.replace('bg-', '')}40` }}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2 mb-1">
                          <Star size={14} className={rock.priority === 1 ? "text-amber-400" : rock.priority === 2 ? "text-slate-400" : "text-slate-600"} />
                          <h3 className="font-semibold text-slate-100 text-sm">{rock.title}</h3>
                        </div>
                        <button onClick={() => handleDeleteRock(rock.id)} className="text-slate-500 hover:text-red-400">
                          <X size={14} />
                        </button>
                      </div>
                      <p className="text-xs text-slate-400">{rock.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // Non-admin sees their department rocks
        <div>
          <h2 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${DEPARTMENTS[userDepartment]?.color}`}></div>
            {DEPARTMENTS[userDepartment]?.label} Rocks
          </h2>
          {myDeptRocks.filter(r => r.department === userDepartment).length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {myDeptRocks.filter(r => r.department === userDepartment).map(rock => (
                <div key={rock.id} className="p-3 bg-slate-800/30 border border-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Star size={14} className={rock.priority === 1 ? "text-amber-400" : "text-slate-600"} />
                    <h3 className="font-semibold text-slate-100 text-sm">{rock.title}</h3>
                  </div>
                  <p className="text-xs text-slate-400">{rock.description}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No department rocks set for this month yet.</p>
          )}
        </div>
      )}

      {/* Empty State */}
      {rocks.length === 0 && (
        <div className="text-center py-8">
          <Target className="w-12 h-12 mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400">No rocks set for {MONTHS[selectedMonth - 1]} {selectedYear}</p>
          <p className="text-sm text-slate-500 mt-1">Share your ideas using the button above!</p>
        </div>
      )}

      {/* Submit Idea Dialog */}
      <Dialog open={showIdeaDialog} onOpenChange={setShowIdeaDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Lightbulb className="text-amber-400" size={20} />
              Share Your Idea
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Submit your thoughts for {MONTHS[selectedMonth - 1]}'s rocks. Admin will review and create the official goals.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="What should our team focus on this month? Share your idea..."
              value={newIdea}
              onChange={(e) => setNewIdea(e.target.value)}
              className="min-h-[100px] bg-slate-900 border-slate-600 text-white"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIdeaDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitIdea} disabled={submitting || !newIdea.trim()} className="bg-amber-600 hover:bg-amber-700">
              <Send size={14} className="mr-1" />
              Submit Idea
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Rock Dialog (Admin) */}
      <Dialog open={showRockDialog} onOpenChange={setShowRockDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Target className="text-indigo-400" size={20} />
              Create New Rock
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Set a goal for {MONTHS[selectedMonth - 1]} {selectedYear}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div>
              <Label className="text-xs text-slate-400">Title</Label>
              <Input
                placeholder="Rock title..."
                value={newRock.title}
                onChange={(e) => setNewRock({ ...newRock, title: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Description</Label>
              <Textarea
                placeholder="Describe the goal..."
                value={newRock.description}
                onChange={(e) => setNewRock({ ...newRock, description: e.target.value })}
                className="min-h-[80px] bg-slate-900 border-slate-600 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Department</Label>
                <Select value={newRock.department} onValueChange={(v) => setNewRock({ ...newRock, department: v })}>
                  <SelectTrigger className="bg-slate-900 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {Object.entries(DEPARTMENTS).map(([key, val]) => (
                      <SelectItem key={key} value={key} className="text-slate-200">
                        {val.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Priority</Label>
                <Select value={newRock.priority.toString()} onValueChange={(v) => setNewRock({ ...newRock, priority: Number(v) })}>
                  <SelectTrigger className="bg-slate-900 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="1" className="text-slate-200">High</SelectItem>
                    <SelectItem value="2" className="text-slate-200">Medium</SelectItem>
                    <SelectItem value="3" className="text-slate-200">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRockDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateRock} disabled={submitting || !newRock.title.trim()} className="bg-indigo-600 hover:bg-indigo-700">
              Create Rock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyRocks;
